import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext, useApi } from '@repo-level/core-front';

import { BonanzaRestrictedGroups, someGroupMember } from 'helpers/user';
import {
  BonanzaStatus,
  getUserBonanzaGroup,
  getUserReward,
  checkIsBonanzaPromoAvailable,
  bonanzaPromoProgressPercent,
} from 'helpers/bonanza';
import { amountWithCurrencySymbol } from 'helpers/currency';
import {
  bonanzaPromo,
  BonanzaPromoApi,
  BonanzaStatusItems,
} from 'api/cms/bonanzaPromo';

interface BonanzaContextData {
  userBonanzaGroup: BonanzaStatus;
  bonanzaReward: string;
  bonanzaData: BonanzaPromoApi | null;
  bonanzaLevelItem: UpdatedBonanzaLevelItems | null;
  isBonanzaPromoAvailable: boolean;
  isLogged: boolean;
}

interface UpdatedBonanzaLevelItems
  extends Omit<BonanzaStatusItems, 'promoLevel'> {
  progressPercent: number;
}

interface BonanzaContextActions {
  updateBoxData: (language: string) => void;
  markAsLogged: () => void;
}

const initialValues = {
  userBonanzaGroup: BonanzaStatus.LEVEL0,
  bonanzaReward: '',
  bonanzaData: null,
  bonanzaLevelItem: null,
  isBonanzaPromoAvailable: false,
  isLogged: localStorage.getItem('bonanza_logged') === 'true',
};

export const BonanzaContext = createContext<
  BonanzaContextData & BonanzaContextActions
>(null as any);

export const BonanzaContextProvider: FC = ({ children }) => {
  const { user } = useContext(AuthContext);
  const {
    i18n: { language },
  } = useTranslation();

  const [state, setState] = useState<BonanzaContextData>(initialValues);
  const [
    requestBonanzaPromo,
    { data: bonanzaCMSData, error: bonanzaError },
  ] = useApi(bonanzaPromo);

  const updateBoxData = useCallback(
    (language: string) => {
      if (bonanzaCMSData) {
        const userBonanzaGroup = getUserBonanzaGroup(
          bonanzaCMSData.promoPrefix,
          user?.statuses
        );

        const userReward = getUserReward(language, user);

        const isPromoAvailable =
          !someGroupMember(BonanzaRestrictedGroups)(user) &&
          checkIsBonanzaPromoAvailable(bonanzaCMSData, user);

        const updatedBonanzaStatusData = bonanzaCMSData.bonanzaStatus.reduce(
          (acc, { character, label, labelMob, promoLevel }) => {
            const promoLevelKey = promoLevel as keyof typeof BonanzaStatus;
            const bonanzaStatusKey = BonanzaStatus[promoLevelKey];

            acc[bonanzaStatusKey] = {
              progressPercent: bonanzaPromoProgressPercent[bonanzaStatusKey],
              character,
              label,
              labelMob,
            };

            return acc;
          },
          {} as Record<BonanzaStatus, UpdatedBonanzaLevelItems>
        );

        setState((prevState) => ({
          ...prevState,
          userBonanzaGroup,
          bonanzaReward: amountWithCurrencySymbol(
            userReward.currency,
            userReward.reward
          ),
          bonanzaData: bonanzaCMSData,
          bonanzaLevelItem: updatedBonanzaStatusData[userBonanzaGroup],
          isBonanzaPromoAvailable: isPromoAvailable,
        }));
      } else if (bonanzaError) {
        setState((prevState) => ({
          ...prevState,
          isBonanzaPromoAvailable: false,
        }));
      }
    },
    [bonanzaCMSData, bonanzaError, user]
  );

  useEffect(() => {
    requestBonanzaPromo(language);
  }, [language, requestBonanzaPromo]);

  useEffect(() => {
    updateBoxData(language);
  }, [language, bonanzaCMSData, updateBoxData]);

  const markAsLogged = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isLogged: true,
    }));
  }, []);

  return (
    <BonanzaContext.Provider
      value={{
        ...state,
        updateBoxData,
        markAsLogged,
      }}
    >
      {children}
    </BonanzaContext.Provider>
  );
};
